import type {
  FriendRequestDto,
  ItemDto,
  LinkPreviewResponse,
  ListDto,
  PublicUser,
  SelfUser,
} from '@wishare/shared';

/**
 * Клиент API.
 *
 * Одна точка входа для всех запросов: здесь же разбираются ошибки сервера и
 * превращаются в человеческий текст. Компоненты не знают ни про коды ответов,
 * ни про формат ошибок — они получают либо данные, либо понятное сообщение.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Пользователь не вошёл или сессия истекла. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

/**
 * Сколько ждём ответа, прежде чем считать связь оборванной.
 *
 * Без этого срока запрос в мобильной сети может не кончиться никогда: соединение
 * не рвётся, а просто замирает, и `fetch` не отвечает ни успехом, ни ошибкой.
 * Снаружи это выглядит хуже любой ошибки — приложение показывает «Открываем
 * Загадарь…» и остаётся так навсегда, а человек не понимает, сломалось оно
 * или ещё думает.
 *
 * Пятнадцать секунд — это заведомо больше обычного ответа (десятки миллисекунд)
 * и заведомо меньше терпения человека, стоящего с телефоном в руке.
 */
const СРОК_ОТВЕТА_МС = 15_000;

/**
 * Срок для разбора ссылки — отдельный и намеренно большой.
 *
 * Разбор ходит на чужой сайт, иногда поднимает настоящий браузер, и полминуты
 * там дело обычное. Общий срок оборвал бы его на середине, и человек получил бы
 * «нет связи» вместо карточки, которая почти собралась.
 */
const СРОК_РАЗБОРА_МС = 60_000;

/** Срок на отправку фотографии: мегабайты из мобильного интернета — это долго. */
const СРОК_ЗАГРУЗКИ_МС = 120_000;

/**
 * Сигнал, который сам сработает через заданное время.
 *
 * `AbortSignal.timeout` есть не везде, где живёт наше приложение: в Safari он
 * появился только в 16-й версии, а на телефонах, которые не обновляли, стоит
 * и более старая. Поэтому при его отсутствии заводим таймер руками — иначе
 * починка не дошла бы ровно до тех, у кого связь и так хуже всех.
 */
function сигналСоСроком(мс: number): { signal: AbortSignal; отменить(): void } {
  const встроенный = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout;
  if (typeof встроенный === 'function') {
    return { signal: встроенный.call(AbortSignal, мс), отменить() {} };
  }
  const управление = new AbortController();
  const таймер = setTimeout(() => управление.abort(), мс);
  return { signal: управление.signal, отменить: () => clearTimeout(таймер) };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  shareToken?: string | null,
  срокМс: number = СРОК_ОТВЕТА_МС,
): Promise<T> {
  let response: Response;

  const срок = сигналСоСроком(срокМс);
  try {
    response = await fetch(`/api${path}`, {
      method,
      // Без этого браузер не пошлёт cookie сессии.
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(shareToken ? { 'x-share-token': shareToken } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: срок.signal,
    });
  } catch {
    // Сюда попадаем и при обрыве сети, и когда ответа не дождались: для человека
    // это одно и то же — связи нет. Самый частый случай в мобильном интернете.
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером. Проверьте интернет и попробуйте снова.');
  } finally {
    срок.отменить();
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Что-то пошло не так. Попробуйте ещё раз.',
      error?.details,
    );
  }

  return payload as T;
}

const get = <T>(path: string, shareToken?: string | null): Promise<T> => request<T>('GET', path, undefined, shareToken);
const post = <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body);
const patch = <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body);
const del = <T>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body);

export interface ListWithItems {
  list: ListDto;
  items: ItemDto[];
}

export interface MyGift {
  id: string;
  status: 'reserved' | 'purchased';
  quantity: number;
  note: string | null;
  anonymous: boolean;
  createdAt: string;
  item: {
    id: string;
    title: string;
    imageUrl: string | null;
    priceMinor: number | null;
    currency: string | null;
    url: string | null;
  };
  list: { id: string; title: string };
  recipient: { id: string; displayName: string; username: string | null; avatarUrl: string | null };
}

export interface FriendsResponse {
  friends: FriendRequestDto[];
  incoming: FriendRequestDto[];
  outgoing: FriendRequestDto[];
}

export interface UpcomingBirthday {
  user: PublicUser;
  birthday: string | null;
  /** Сколько дней осталось. Ноль — сегодня. */
  daysUntil: number;
}

export const api = {
  auth: {
    me: () => get<SelfUser>('/auth/me'),
    login: (email: string, password: string) => post<SelfUser>('/auth/login', { email, password }),
    register: (input: { email: string; password: string; displayName: string; username?: string }) =>
      post<SelfUser>('/auth/register', input),
    logout: () => post<{ ok: true }>('/auth/logout'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: true }>('/auth/change-password', { currentPassword, newPassword }),
    forgotPassword: (email: string) => post<{ ok: true; message: string }>('/auth/forgot', { email }),
    deleteAccount: (password: string) => del<{ ok: true }>('/auth/account', { password }),
    resetPassword: (token: string, newPassword: string) =>
      post<SelfUser>('/auth/reset', { token, newPassword }),
  },

  users: {
    updateProfile: (input: Record<string, unknown>) => patch<PublicUser>('/users/me', input),
    search: (query: string) => get<PublicUser[]>(`/users/search?q=${encodeURIComponent(query)}`),
    profile: (username: string) =>
      get<{ user: PublicUser; lists: ListDto[] }>(`/users/@${encodeURIComponent(username)}`),
  },

  friends: {
    all: () => get<FriendsResponse>('/friends'),
    birthdays: () => get<UpcomingBirthday[]>('/friends/birthdays'),
    request: (input: { username?: string; userId?: string }) =>
      post<{ status: string; user: PublicUser }>('/friends/request', input),
    accept: (userId: string) => post<{ ok: true }>(`/friends/${userId}/accept`),
    decline: (userId: string) => post<{ ok: true }>(`/friends/${userId}/decline`),
    remove: (userId: string) => del<{ ok: true }>(`/friends/${userId}`),
    block: (userId: string) => post<{ ok: true }>(`/friends/${userId}/block`),
  },

  lists: {
    mine: () => get<ListDto[]>('/lists'),
    feed: () => get<ListDto[]>('/lists/feed'),
    one: (listId: string) => get<ListWithItems>(`/lists/${listId}`),
    byShareToken: (token: string) => get<ListWithItems>(`/share/${encodeURIComponent(token)}`),
    create: (input: Record<string, unknown>) => post<ListDto>('/lists', input),
    update: (listId: string, input: Record<string, unknown>) => patch<ListDto>(`/lists/${listId}`, input),
    remove: (listId: string) => del<{ ok: true }>(`/lists/${listId}`),
    rotateShare: (listId: string) => post<ListDto>(`/lists/${listId}/rotate-share`),
    reorder: (listId: string, itemIds: string[]) => post<{ ok: true }>(`/lists/${listId}/reorder`, { itemIds }),
  },

  items: {
    create: (listId: string, input: Record<string, unknown>) => post<ItemDto>(`/lists/${listId}/items`, input),
    update: (itemId: string, input: Record<string, unknown>) => patch<ItemDto>(`/items/${itemId}`, input),
    remove: (itemId: string) => del<{ ok: true }>(`/items/${itemId}`),
  },

  reservations: {
    create: (itemId: string, input: Record<string, unknown>) =>
      post<ItemDto>(`/items/${itemId}/reservation`, input),
    update: (itemId: string, input: Record<string, unknown>) =>
      patch<ItemDto>(`/items/${itemId}/reservation`, input),
    remove: (itemId: string) => del<ItemDto>(`/items/${itemId}/reservation`),
    mine: () => get<MyGift[]>('/reservations/mine'),
  },

  links: {
    preview: (url: string, refresh = false) =>
      request<LinkPreviewResponse>('POST', '/links/preview', { url, refresh }, null, СРОК_РАЗБОРА_МС),
    /*
     * Только вопрос «это у меня уже есть?», без похода в чужой сайт.
     * Нужен карточкам, которые собрал сам браузер человека: разбирать их
     * заново нечего, а предупредить о повторе всё равно надо.
     */
    duplicates: (url: string) =>
      post<{ duplicates: LinkPreviewResponse['duplicates'] }>('/links/duplicates', { url }),
  },

  uploads: {
    /**
     * Загрузка картинки. Идёт мимо общего JSON-клиента: тело — это файл,
     * а не JSON, поэтому собираем FormData и шлём напрямую.
     */
    async image(file: File): Promise<{ url: string }> {
      const form = new FormData();
      form.append('file', file);

      let response: Response;
      /*
       * Свой срок, и намеренно щедрый.
       *
       * Фотография с телефона весит мегабайты, а отправляют её из того же
       * мобильного интернета, в котором приложение и зависало: полминуты
       * на снимок там дело обычное, и общий срок обрывал бы честную загрузку
       * на середине. Но без срока вовсе замершее соединение снова означало бы
       * вечное ожидание — ровно то, что мы и чиним.
       */
      const срок = сигналСоСроком(СРОК_ЗАГРУЗКИ_МС);
      try {
        response = await fetch('/api/uploads/image', {
          method: 'POST',
          credentials: 'include',
          body: form,
          signal: срок.signal,
        });
      } catch {
        throw new ApiError(0, 'NETWORK', 'Нет связи с сервером. Проверьте интернет.');
      } finally {
        срок.отменить();
      }

      const payload = (await response.json().catch(() => null)) as
        | { url?: string; error?: { code?: string; message?: string } }
        | null;

      if (!response.ok || !payload?.url) {
        throw new ApiError(
          response.status,
          payload?.error?.code ?? 'UPLOAD_FAILED',
          payload?.error?.message ?? 'Не удалось загрузить картинку.',
        );
      }

      return { url: payload.url };
    },
  },
};
